import { GIFT_CARD_HEIGHT_MM, GIFT_CARD_WIDTH_MM, type GiftCardBrand, type GiftCardVoucher } from "./gift-card-pdf";

export const GIFT_CARD_IMAGE_WIDTH = 1284;
export const GIFT_CARD_IMAGE_HEIGHT = Math.round(GIFT_CARD_IMAGE_WIDTH * GIFT_CARD_HEIGHT_MM / GIFT_CARD_WIDTH_MM);

function fitText(ctx: CanvasRenderingContext2D, text: string, size: number, width: number, weight = "bold") {
  while (size > 16) {
    ctx.font = `${weight} ${size}px Arial, sans-serif`;
    if (ctx.measureText(text).width <= width) return;
    size -= 1;
  }
  ctx.font = `${weight} ${size}px Arial, sans-serif`;
  if (ctx.measureText(text).width > width) throw new Error("The card text is too long to share legibly.");
}

export async function createGiftCardImage(
  voucher: GiftCardVoucher,
  store: GiftCardBrand,
  loadLogo?: () => Promise<string>,
) {
  const canvas = document.createElement("canvas");
  canvas.width = GIFT_CARD_IMAGE_WIDTH;
  canvas.height = GIFT_CARD_IMAGE_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Your browser cannot create gift card images.");
  ctx.fillStyle = "#0b362e";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "#d3b370";
  ctx.lineWidth = 4;
  ctx.strokeRect(30, 30, canvas.width - 60, canvas.height - 60);

  let logoIncluded = false;
  if (store?.logoUrl && loadLogo) {
    try {
      const image = new Image();
      image.src = await loadLogo();
      await image.decode();
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(84, 84, 170, 170);
      const scale = Math.min(146 / image.naturalWidth, 146 / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      ctx.drawImage(image, 169 - width / 2, 169 - height / 2, width, height);
      logoIncluded = true;
    } catch {
      // Optional branding must not prevent sharing a valid voucher.
    }
  }
  const brandX = logoIncluded ? 288 : 84;
  ctx.fillStyle = "#ffffff";
  const brand = store?.name?.trim() || "Your Store";
  fitText(ctx, brand, 48, canvas.width - brandX - 84);
  ctx.fillText(brand, brandX, 152);
  ctx.fillStyle = "#e0c790";
  ctx.font = "bold 32px Arial, sans-serif";
  ctx.fillText("GIFT VOUCHER", 84, 332);
  ctx.fillStyle = "#ffffff";
  const amount = `NGN ${Number(voucher.value).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  fitText(ctx, amount, 100, canvas.width - 168);
  ctx.fillText(amount, 84, 460);
  ctx.fillStyle = "#e0ebe6";
  ctx.font = "24px Arial, sans-serif";
  ctx.fillText("VOUCHER NUMBER", 84, 558);
  ctx.fillStyle = "#ffffff";
  fitText(ctx, voucher.code, 44, canvas.width - 168);
  ctx.fillText(voucher.code, 84, 622);
  ctx.fillStyle = "#e0ebe6";
  ctx.font = "24px Arial, sans-serif";
  ctx.fillText("Voucher balance is verified at checkout", 84, canvas.height - 70);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(result => result ? resolve(result) : reject(new Error("Could not create the gift card image.")), "image/png");
  });
  const safeCode = voucher.code.replace(/[^a-z0-9-]/gi, "-");
  return { file: new File([blob], `gift-card-${safeCode}.png`, { type: "image/png" }), logoIncluded };
}
